import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
} from "components/ui/card";
import { cn } from "@/lib/utils";
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@/components/ui/button";

export interface CoreEntry {
  label: string;
  /** An app route, or an absolute URL for anything we host outside Hub. */
  href: string;
}

const isExternal = (href: string) => href.startsWith("http");

export interface CoreShowcase {
  labels: string[];
  render: (active: number) => ReactNode;
}

type CoreIcon = ComponentType<{ className?: string }>;

/** The card's branded lockup, standing in for the icon and title at every width. */
export type CoreLockup = ComponentType<{ className?: string }>;

export interface CoreFunction {
  id: string;
  title: string;
  what: string;
  why: string;
  entries: CoreEntry[];
  icon: CoreIcon;
  color: string;
  tint?: string;
  lockup?: CoreLockup;
  soon?: boolean;
  /** A single CTA shown in place of the entry links, for products not yet available. */
  waitlistCta?: ComponentType;
  showcase?: CoreShowcase;
}

const PANEL_MS = 5000;

const useShowcaseRotation = (
  count: number,
  panel: React.RefObject<HTMLDivElement | null>,
) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const element = panel.current;
    if (count < 2 || !element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let timer: number | undefined;
    let onScreen = false;

    const sync = () => {
      window.clearInterval(timer);
      timer = undefined;
      if (onScreen && !document.hidden) {
        timer = window.setInterval(
          () => setActive((i) => (i + 1) % count),
          PANEL_MS,
        );
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        sync();
      },
      { threshold: 0.2 },
    );
    observer.observe(element);
    document.addEventListener("visibilitychange", sync);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.clearInterval(timer);
    };
  }, [count, panel]);

  return active;
};

/**
 * A home-page card for one of Hub's core functions. It reveals more of itself the
 * more room it has: title only when tiny, plus `what` once it is wide enough, plus
 * its paragraph and entry buttons once it is large enough.
 *
 * The entry buttons carry the navigation. The card itself is only clickable once
 * it has shrunk far enough that the container query hides those buttons — then it
 * stands in for the first of them, so a collapsed card is never a dead end.
 */
export const CoreCard = ({
  fn,
  editing,
}: {
  fn: CoreFunction;
  editing?: boolean;
}) => {
  const history = useHistory();
  const Icon = fn.icon;
  const Lockup = fn.lockup;
  const WaitlistCta = fn.waitlistCta;
  const panel = useRef<HTMLDivElement>(null);
  const active = useShowcaseRotation(fn.showcase?.labels.length ?? 0, panel);

  // The card acts as a button only while its entries are hidden by the container
  // query; forwarding to the first one covers both navigation and the waitlist.
  const root = useRef<HTMLDivElement>(null);
  const entries = useRef<HTMLDivElement>(null);
  const [entriesHidden, setEntriesHidden] = useState(false);

  useEffect(() => {
    const rootEl = root.current;
    const entriesEl = entries.current;
    if (!rootEl || !entriesEl) return;
    const update = () =>
      setEntriesHidden(window.getComputedStyle(entriesEl).display === "none");
    update();
    const observer = new ResizeObserver(update);
    observer.observe(rootEl);
    return () => observer.disconnect();
  }, []);

  // Standing in for the buttons would fight the drag handlers while editing.
  const collapsed = entriesHidden && !editing;

  const activateFirstEntry = () =>
    entries.current?.querySelector<HTMLElement>("button, a")?.click();

  return (
    <div ref={root} className={cn("home-core h-full", fn.tint)}>
      <Card
        className={cn(
          "relative h-full flex-row gap-0 overflow-hidden bg-card p-0",
          collapsed && "hover:bg-muted cursor-pointer transition-colors",
        )}
        {...(collapsed && {
          role: "button",
          tabIndex: 0,
          "aria-label": fn.title,
          onClick: activateFirstEntry,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              activateFirstEntry();
            }
          },
        })}
      >
        <div className="relative flex min-w-0 flex-1 flex-col gap-4 py-4">
          {fn.soon && (
            <span
              aria-hidden="true"
              data-testid={`home-core-${fn.id}-coming-soon`}
              className="pointer-events-none absolute top-5 right-6 text-3xl font-semibold tracking-tight whitespace-nowrap text-muted-foreground/20 select-none"
            >
              Coming soon
            </span>
          )}
          <CardHeader>
            {Lockup && (
              <Lockup
                className="home-core-lockup"
                data-testid={`home-core-${fn.id}-lockup`}
              />
            )}
            <CardIcon
              variant="default"
              className={cn(
                fn.color,
                "text-background rounded-lg p-2.5",
                Lockup && "home-core-mark",
              )}
            >
              <Icon className="size-6" />
            </CardIcon>
            <CardTitle
              className={cn(Lockup && "home-core-mark")}
              data-testid={`home-core-${fn.id}-title`}
            >
              {fn.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-2">
            <CardDescription
              className="home-core-what max-w-[26ch] text-base leading-snug font-medium text-balance text-foreground"
              data-testid={`home-core-${fn.id}-what`}
            >
              {fn.what}
            </CardDescription>
            <CardDescription
              className="home-core-why max-w-[46ch]"
              data-testid={`home-core-${fn.id}-why`}
            >
              {fn.why}
            </CardDescription>
            {fn.showcase && (
              <div className="home-core-modules text-muted-foreground flex-wrap items-center gap-1.5 font-mono text-[0.6875rem] tracking-[0.04em] uppercase">
                {fn.showcase.labels.map((label, i) => (
                  <Fragment key={label}>
                    {i > 0 && <span className="opacity-45">/</span>}
                    <span
                      data-testid={`home-core-${fn.id}-module`}
                      data-active={i === active}
                      className={cn(
                        "transition-colors",
                        i === active && "text-foreground",
                      )}
                    >
                      {label}
                    </span>
                  </Fragment>
                ))}
              </div>
            )}
            <div
              ref={entries}
              className="home-core-entries mt-auto flex-wrap gap-x-4 gap-y-1"
            >
              {WaitlistCta ? (
                <WaitlistCta />
              ) : (
                fn.entries.map((entry) => (
                  <Button
                    variant="outline"
                    key={entry.href}
                    type="button"
                    data-testid={`home-core-${fn.id}-entry`}
                    onClick={() => {
                      if (isExternal(entry.href)) {
                        window.open(
                          entry.href,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      } else {
                        history.push(entry.href);
                      }
                    }}
                  >
                    {entry.label}
                  </Button>
                ))
              )}
            </div>
          </CardContent>
        </div>

        {fn.showcase && (
          <div
            ref={panel}
            aria-hidden="true"
            data-testid={`home-core-${fn.id}-plate`}
            className="home-core-plate relative w-2/5 max-w-80 min-w-60 shrink-0 border-l bg-muted"
          >
            {fn.showcase.render(active)}
          </div>
        )}
      </Card>
    </div>
  );
};
